import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { toast } from "Layout/slice";
import { Loader } from "components/Loader";
import { trackEvent } from "utils/tracking";
import { Button } from "components/Button";
import { useNavigation } from "utils/navigate";
import { USER_FLAGS } from "utils/constants";
import { strategyPlayV2 } from "assets/icons";
import { parseErrorMessage, contains } from "utils";
import { updateUserFlags } from "Layout/slice/thunk";
import { SuccessModal } from "shared/Modal/SuccessModal";
import { StartStrategyModalContainer } from "./StartStrategyModal.styles";
import { startStrategy, reactivateStrategy } from "features/Strategy/service";
import { setShowFeatureDetailsKey, setWalkthrough } from "Layout/slice/index";
import { welcomeWalkthroughSelect } from "Layout/slice/selector";
import { usePortfolioDetails } from "features/Dashboard/hook";
import { useAccountDetails } from "features/Dashboard/hook/useAccountDetails";
import { useActiveStrategies } from "features/Dashboard/hook/useActiveStrategies";

const StartStrategyModal = ({
  show = true,
  onHide,
  strategyId,
  portfolioId,
  allocationPrice,
  onStartedStrategy,
  status,
  subscriptionId,
  strategyName = "",
  toggleAllocationModal,
  allocationAvailableBalance,
}) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { navigateToV2StrategyList, navigateToV2PortfolioDetail } =
    useNavigation();
  const isWelcomeWalkthrough = useSelector(welcomeWalkthroughSelect);

  const [loading, setLoading] = useState(false);
  const [successText, setSuccessText] = useState("");
  const [showStartedStrategyModal, setShowStartedStrategyModal] =
    useState(false);

  const { refetch: refetchAccountDetails } = useAccountDetails();
  const { refetch: refetchActiveStrategies } = useActiveStrategies();
  const { refetch: refetchPortfolioDetails } = usePortfolioDetails(portfolioId);

  useEffect(() => {
    if (isWelcomeWalkthrough && !loading) {
      setTimeout(() => {
        dispatch(setShowFeatureDetailsKey("startStrategy"));
        dispatch(setWalkthrough({ run: true, stepIndex: 0, scrollTo: false }));
      }, 500);
    }
  }, [loading]);

  const onStartStrategy = async () => {
    try {
      setLoading(true);

      trackEvent({
        name: "activate_strategy",
        label: "Strategy",
        strategy_id: strategyId || id,
        strategy_name: strategyName,
        portfolio_id: portfolioId,
      });

      if (status && status === "Paused") {
        const response = await reactivateStrategy(subscriptionId);

        setSuccessText(response || "Your strategy has been started!");
        setShowStartedStrategyModal(true);
      } else {
        const allocationPercentage =
          (100 * allocationPrice) / allocationAvailableBalance;

        const data = {
          portfolioId,
          strategyId: strategyId || id,
          allocationPercentage,
          allocationPrice,
        };
        await startStrategy(data);
        hideStrategyStartedModal();
      }
      dispatch(
        toast({
          type: "info",
          body: "Your strategy was successfully deployed",
        })
      );
      dispatch(updateUserFlags(USER_FLAGS.HAS_INVESTED));
    } catch (error) {
      const msg = parseErrorMessage(error);
      if (contains(msg, "already connected")) {
        navigateToV2StrategyList("activeTab=active");
      }
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
      onHide();
      if (toggleAllocationModal) toggleAllocationModal(true);
    }
  };

  const hideStrategyStartedModal = async () => {
    setShowStartedStrategyModal(false);

    await Promise.all([
      refetchAccountDetails(),
      refetchPortfolioDetails(),
      refetchActiveStrategies(),
    ]);

    if (onStartedStrategy) {
      onStartedStrategy(portfolioId);
      return;
    }

    const options = {
      search: `?activeTab=strategyManagement`,
    };

    navigateToV2PortfolioDetail(portfolioId, options);
  };

  return (
    <>
      {!showStartedStrategyModal && (
        <StartStrategyModalContainer
          heading=""
          show={show}
          onHide={onHide}
          headingClass="modal-heading"
        >
          <div>
            <div className="d-flex justify-content-center pb-2">
              <img src={strategyPlayV2} className="img" />
            </div>
            <h2 className="heading">Start Strategy</h2>

            <div className="strategy-modal-header">
              <p className="description">
                {strategyName} will automate trades on your behalf. You can
                pause or stop this strategy at any time
              </p>
            </div>
          </div>

          <div className="strategy-modal-footer">
            <Button
              onClick={onHide}
              label="Cancel"
              className="cancel-btn button-outlined"
              disabled={loading}
            />
            <Button
              onClick={onStartStrategy}
              label="Start"
              className="start-btn wt-btn-start"
              disabled={loading}
            >
              <div className="d-flex align-items-center justify-content-center">
                {loading && <Loader className="on-btn mx-2" />}
                Start
              </div>
            </Button>
          </div>
        </StartStrategyModalContainer>
      )}
      {showStartedStrategyModal && (
        <SuccessModal
          description={successText}
          icon={strategyPlayV2}
          className="started-strat-modal"
          onHide={hideStrategyStartedModal}
        />
      )}
    </>
  );
};

export default StartStrategyModal;

StartStrategyModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  strategyId: PropTypes.string,
  portfolioId: PropTypes.string,
  allocationPrice: PropTypes.any,
  onStartedStrategy: PropTypes.func,
  status: PropTypes.string,
  subscriptionId: PropTypes.string,
  strategyName: PropTypes.string,
  toggleAllocationModal: PropTypes.func,
  allocationAvailableBalance: PropTypes.number,
};

StartStrategyModal.displayName = "StartStrategyModal";
